import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { DEFAULT_OFFSET_PAGE_SIZE, OFFSET_PAGE_SIZES, } from '@wit-support/admin-contracts';
/** Поиск роутера → строки; пустое и нечитаемое отбрасывается. */
export function normalizeSearch(raw) {
    const out = {};
    if (raw === null || typeof raw !== 'object')
        return out;
    for (const [k, v] of Object.entries(raw)) {
        if (typeof v === 'string' && v.trim() !== '')
            out[k] = v;
        else if (typeof v === 'number' && Number.isFinite(v))
            out[k] = String(v);
        else if (typeof v === 'boolean')
            out[k] = String(v);
    }
    return out;
}
/** Применить правку к поиску роутера (значения — как есть, не строками). */
export function applySearchPatch(prev, patch) {
    const next = prev !== null && typeof prev === 'object' ? { ...prev } : {};
    for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === null || v === '' || v === false)
            delete next[k];
        else
            next[k] = v;
    }
    return next;
}
/**
 * Сбрасывать ли прокрутку при смене поиска: наверх — только переход на другую
 * страницу таблицы (новые строки читают сверху), а не смена фильтра.
 */
export function scrollResetFor(patch) {
    const page = patch['page'];
    return page !== undefined && page !== null && page !== '' && page !== false;
}
/**
 * Текущий поиск (строками) и запись правки. По умолчанию — новая запись
 * истории («назад» возвращает прошлый фильтр); `replace` — для набора текста
 * и прижатия страницы к последней.
 */
export function useUrlSearch() {
    const raw = useSearch({ strict: false });
    const navigate = useNavigate();
    const update = useCallback((patch, opts) => {
        void navigate({
            to: '.',
            search: (prev) => applySearchPatch(prev, patch),
            resetScroll: scrollResetFor(patch),
            ...(opts?.replace ? { replace: true } : {}),
        });
    }, [navigate]);
    return [normalizeSearch(raw), update];
}
export function readString(v) {
    if (v === undefined || v === null)
        return undefined;
    const s = String(v).trim();
    return s === '' ? undefined : s;
}
export function readEnum(options, v) {
    const s = readString(v);
    return s !== undefined && options.includes(s) ? s : undefined;
}
/** Целое ≥ 1 или undefined. */
export function readPositiveInt(v) {
    const n = typeof v === 'number' ? v : Number(readString(v));
    return Number.isInteger(n) && n >= 1 ? n : undefined;
}
/** Номер страницы с 1; мусор — 1. */
export function readPage(v) {
    return readPositiveInt(v) ?? 1;
}
/** Размер страницы из 25/50/100/200; иначе 50. */
export function readPageSize(v) {
    const n = readPositiveInt(v);
    return n !== undefined && OFFSET_PAGE_SIZES.includes(n)
        ? n
        : DEFAULT_OFFSET_PAGE_SIZE;
}
//# sourceMappingURL=url-search.js.map